import React from 'react';
import Typography from "@mui/material/Typography";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import {ROLE_CHAT_MESSAGE} from 'utils/constants';
import ChatAvatar from '../avatar';
import {useLazyGetDeclarationsQuery} from "../../../../../../../../../../../../../../../api/dictionary";

import {COLORS} from '../../../../../../../../../../../../../../../config/constants';

import './message.scss';

function ChatMessageItem({msg, handleApplyCode} : ChatMessageItemProps) {

  const [ declarationsRequest ] = useLazyGetDeclarationsQuery()

  const preprocessHSLinks = (text: string): string => {
    const regex = /((?:\d[ .]?){9}\d)/g;

    return text.replace(regex, (rawMatch) => {
      const cleanCode = rawMatch.replace(/\D/g, "");
      if (cleanCode.length !== 10) return rawMatch;
      return `<a href="https://hs/${cleanCode}" class="chat-message__hs-code">${rawMatch}</a>`;
    });
  };

  const preprocessMarkdown = (text: string): string => {
    return text.replace(/\*\*(\d+\.)/g, '$1');
  };

  const combinedPreprocess = (text: string): string => {
    return preprocessHSLinks(preprocessMarkdown(text));
  };

  const renderAssistantMessage = (text: string) => {

    return (
      <div className='chat-message__markdown'>
        <ReactMarkdown
          remarkPlugins={[ remarkGfm ]}
          rehypePlugins={[ rehypeRaw ]}
          components={{
            h1: ({node, ref, ...props}) => <Typography component='h1' variant='inherit' sx={{ marginBottom: '8px' }} {...props} />,
            h2: ({node, ref, ...props}) => <Typography component='h2' variant='inherit' sx={{ marginBottom: '8px' }} {...props} />,
            h3: ({node, ref, ...props}) => <Typography component='h3' variant='inherit' sx={{ marginBottom: '8px' }} {...props} />,
            p: ({node, ref, ...props}) => <Typography
              component='p'
              variant='inherit'
              sx={{
                margin: 0,
                whiteSpace: 'pre-line',
                '& + &': {
                  marginTop: '21px'
                }
              }}
              {...props}
            />,
            li: ({node, ref, ...props}) => <Typography
              component='li'
              variant='inherit'
              className='chat-message__list-item'
              {...props}
            />,
            strong: ({node, ref, ...props}) => <Typography component='strong' variant='inherit' sx={{ fontWeight: 700 }} {...props} />,
            ul: ({node, ref, ...props}) => <ul className='chat-message__list chat-message__list_unordered' {...props} />,
            ol: ({node, ref, ...props}) => <ol className='chat-message__list chat-message__list_ordered' {...props} />,
            a: ({ node, ref, ...props }) => {
              if (props.href?.startsWith("https://hs/")) {
                const code = props.href.replace("https://hs/", "");
                return (
                  <Typography
                    component='a'
                    variant='inherit'
                    sx={{
                      color: COLORS.BLUE,
                      fontWeight: 700,
                      lineHeight: '22px'
                    }}
                    className='chat-message__hs-code'
                    role='button'
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleApplyCode(code);
                      declarationsRequest({ code: code });
                    }}
                  >
                    {props.children}
                  </Typography>
                );
              }
              return <Typography component='a' variant='inherit' className='chat-message__link' sx={{ color: COLORS.BLUE }} {...props} />;
            },
            table: ({node, ref, ...props}) => <table className='chat-message__table' {...props} />,
          }}
        >
          {combinedPreprocess(text)}
        </ReactMarkdown>
      </div>
    );

  };

  return <div className={`chat-message chat-message_${msg.role}`}>
    <div className='chat-message__bubble'>
      {msg.role === ROLE_CHAT_MESSAGE.ASSISTANT && <ChatAvatar />}
      <Typography component='div' variant='body2' className='chat-message__text' sx={{
        color: msg.role === ROLE_CHAT_MESSAGE.USER ? COLORS.WHITE : COLORS.BLACK_DARK,
        fontSize: '14px',
        lineHeight: '21px',
        letterSpacing: 0
      }}>
        {msg.isHtml ? msg.content : renderAssistantMessage(msg.content)}
      </Typography>
    </div>
  </div>;
}

export default ChatMessageItem;
